import * as Sentry from '@sentry/capacitor';
import { createContext, ReactNode, useState } from 'react';

import { toast } from '@/components/Toaster/Toast';

import { RecordingTypeInDatabase } from '@/utils/getFirebaseRecordingTypesByIds';
import { ITag } from '@/utils/getFirebaseTag';

import { hubSensorService, sensorService } from '@/controllers/sensorService';

import ClientSessionManager from '@/hooks/useClients/sessionManager';

import { useAnnotationCounts } from '@/contexts/AnnotationCountsContext';
import { useAuthContext } from '@/contexts/authContext';
import { useUserInfoContext } from '@/contexts/userInfoContext';

import { Maybe } from '@/types/Maybe';
import { MovementType } from '@/types/MovementTypes';

export interface Recording {
  id: number;
  movementType: MovementType;
  movementTypeId: string;
  count: number;
  time: string;
  tags?: ITag[];
  state?: 'active' | 'recording';
  deleted: boolean;
}

export interface IActiveSessionStoreContextValue {
  activeSessionId: string | undefined;
  setActiveSession: (id: string, tests: string[]) => void;
  closeActiveSession: () => void;
  recordingTypeIds: string[];
  recordingTypes: RecordingTypeInDatabase[];
  setRecordingTypes: (types: RecordingTypeInDatabase[]) => void;
  activeTestId: string | undefined;
  setActiveTestId: (id: string) => void;
  recordings: Recording[];
  addRecording: (movementType: MovementType, movementTypeId: string) => Recording;
  deleteRecording: (recordingId: number) => void;
  undoDeleteRecording: (recordingId: number) => void;
  updateRecording: (recording: Recording) => Recording;
}

export const ActiveSessionStore = createContext<Maybe<IActiveSessionStoreContextValue>>(null);

export const ActiveSessionContextProvider = ({ children }: { children: ReactNode }) => {
  const [id, setId] = useState<string>();
  const [recordingTypeIds, setRecordingTypeIds] = useState<string[]>([]);
  const [recordingTypes, setRecordingTypes] = useState<RecordingTypeInDatabase[]>([]);
  const [testId, setTestId] = useState<string>();
  const { userInfo } = useUserInfoContext();

  const { state } = useAnnotationCounts();

  const [recordings, setRecordings] = useState<Recording[]>([]);

  const calculateNewId = (movementType: MovementType): number[] => {
    const previousCount = state.get(movementType) ?? 0;
    const newCount = previousCount + 1;

    return [Number.parseInt(`${movementType}${newCount.toString().padStart(2, '0')}`), newCount];
  };

  const addRecording = (movementType: MovementType, movementTypeId: string) => {
    const newId = calculateNewId(movementType);

    const newRecording: Recording = {
      id: newId.at(0) ?? 0,
      time: new Date().toISOString(),
      count: newId.at(1) ?? 1,
      movementType,
      movementTypeId,
      state: 'active',
      deleted: false,
    };

    setRecordings((prev) => [...prev, newRecording]);

    return newRecording;
  };

  const deleteRecording = (recordingId: number) => {
    const recording = recordings.find((recording) => recording.id === recordingId);

    if (recording) {
      sensorService.deleteAnnotation(recording.count, recording.movementType);
      hubSensorService.deleteAnnotation(recording.count, recording.movementType);
    }

    setRecordings((prev) =>
      prev.map((recording) => (recording.id === recordingId ? { ...recording, deleted: true } : recording)),
    );
  };

  const undoDeleteRecording = (recordingId: number) => {
    const recording = recordings.find((recording) => recording.id === recordingId);

    if (recording) {
      sensorService.restoreAnnotation(recording.count, recording.movementType);
      hubSensorService.restoreAnnotation(recording.count, recording.movementType);
    }

    setRecordings((prev) =>
      prev.map((recording) => (recording.id === recordingId ? { ...recording, deleted: false } : recording)),
    );
  };

  const updateRecording = (recording: Recording) => {
    setRecordings((prev) => {
      const updatedRecordings = prev.map((oldRecording) =>
        oldRecording.id === recording.id ? recording : oldRecording,
      );

      return updatedRecordings;
    });

    return recording;
  };

  // When a new session is started, we want to clear the recordings and set the active session id and test ids
  const setActiveSession = (id: string, recordingTypeIds: string[]) => {
    setRecordings([]);
    setId(id);
    setRecordingTypeIds(recordingTypeIds);
  };

  const closeActiveSession = () => {
    setRecordings([]);
    setId(undefined);
    setRecordingTypeIds([]);
    setRecordingTypes([]);
    // Remove the session from the Firebase.
    console.log('Closing Session with ID:', id);
    if (id && userInfo.id)
      void ClientSessionManager.archiveSession(id, userInfo.id).then((result) => {
        if (result === 'success') {
          if (useAuthContext().state.userState?.role === 'ORYX_Admin') {
            toast({
              variant: 'success',
              title: 'Session archived',
              description: 'The session has been archived successfully.',
              duration: 3000,
            });
          }
        } else {
          Sentry.captureException(result);
          if (useAuthContext().state.userState?.role === 'ORYX_Admin') {
            toast({
              variant: 'error',
              title: 'Could not archive session',
              description: 'The session with id ' + id + ' could not be archived. Please contact support.',
              duration: 5000,
            });
          }
        }
      });
  };

  return (
    <ActiveSessionStore.Provider
      value={{
        activeSessionId: id,
        setActiveSession,
        closeActiveSession,
        recordingTypeIds,
        recordingTypes,
        setRecordingTypes,
        setActiveTestId: setTestId,
        activeTestId: testId,
        recordings,
        addRecording,
        deleteRecording,
        undoDeleteRecording,
        updateRecording,
      }}>
      {children}
    </ActiveSessionStore.Provider>
  );
};
